/**
 * OoxmlTemplatePanel — extracted Section C of MarpTemplateSettings.
 *
 * Upload-only panel for "graphical compilable" Office templates
 * (``.docx`` / ``.xlsx`` / ``.pptx``). The BE detects placeholders like
 * ``{{nome}}`` and stores the file so the AI can later fill it while
 * preserving layout + brand colors. Lists existing user-uploaded ones
 * under the form.
 */

import { Loader2, Upload } from "lucide-react";
import * as React from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import type { I18n } from "@lingui/core";

import { Button, SettingsPanel, cn, toast } from "@ando/ui";

import {
  listOoxmlTemplates,
  uploadOoxmlTemplate,
  type MarpTemplateOwnerScope,
  type OoxmlTemplateItem,
} from "@/features/sources/marp/api";

import { FieldLabel, ScopeRadio } from "./shared";

const OOXML_MAX_BYTES = 25 * 1024 * 1024;

// Slug validation mirrors the BE constraint (same rule as CSS upload).
const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const SLUG_MIN = 3;
const SLUG_MAX = 48;

function validateSlug(slug: string, i18n: I18n): string | null {
  const trimmed = slug.trim();
  if (trimmed.length === 0) return i18n._(msg`Slug obbligatorio`);
  if (trimmed.length < SLUG_MIN)
    return i18n._(msg`Almeno ${SLUG_MIN} caratteri`);
  if (trimmed.length > SLUG_MAX)
    return i18n._(msg`Massimo ${SLUG_MAX} caratteri`);
  if (!SLUG_PATTERN.test(trimmed))
    return i18n._(
      msg`Usa solo lettere minuscole, numeri e trattini (es. \`\`my-blue-deck\`\`)`,
    );
  return null;
}

export const OoxmlTemplatePanel: React.FC = () => {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  const [ooxmlFile, setOoxmlFile] = React.useState<File | null>(null);
  const [ooxmlSlug, setOoxmlSlug] = React.useState("");
  const [ooxmlScope, setOoxmlScope] =
    React.useState<MarpTemplateOwnerScope>("user");
  const [ooxmlError, setOoxmlError] = React.useState<string | null>(null);

  const handlePickOoxml = React.useCallback(
    (file: File | null) => {
      setOoxmlError(null);
      if (!file) {
        setOoxmlFile(null);
        return;
      }
      if (file.size > OOXML_MAX_BYTES) {
        const sizeMb = (file.size / 1024 / 1024).toFixed(1);
        setOoxmlError(
          i18n._(msg`File troppo grande (${sizeMb} MB · max 25 MB)`),
        );
        setOoxmlFile(null);
        return;
      }
      const ok = /\.(docx|xlsx|pptx)$/i.test(file.name);
      if (!ok) {
        setOoxmlError(
          i18n._(msg`Formato non supportato — carica .docx / .xlsx / .pptx`),
        );
        setOoxmlFile(null);
        return;
      }
      setOoxmlFile(file);
    },
    [i18n],
  );

  const ooxmlListQuery = useQuery<{
    readonly templates: readonly OoxmlTemplateItem[];
  }>({
    queryKey: ["ooxml-templates"],
    queryFn: listOoxmlTemplates,
    staleTime: 60_000,
    retry: 1,
  });

  const ooxmlUploadMutation = useMutation({
    mutationFn: () => {
      if (!ooxmlFile) throw new Error(i18n._(msg`Nessun file selezionato`));
      return uploadOoxmlTemplate(ooxmlFile, {
        slug: ooxmlSlug.trim(),
        scope: ooxmlScope,
      });
    },
    onSuccess: (res) => {
      toast.success(
        i18n._(
          msg`Template ${res.slug} caricato (${res.mode}, ${res.placeholders.length} segnaposto)`,
        ),
      );
      void queryClient.invalidateQueries({ queryKey: ["ooxml-templates"] });
      setOoxmlFile(null);
      setOoxmlSlug("");
    },
    onError: (err) => {
      const reason =
        err instanceof Error ? err.message : i18n._(msg`Errore sconosciuto`);
      toast.error(i18n._(msg`Upload fallito: ${reason}`));
    },
  });

  const ooxmlSlugError = React.useMemo(
    () => validateSlug(ooxmlSlug, i18n),
    [ooxmlSlug, i18n],
  );
  const canSubmit =
    ooxmlFile !== null && ooxmlSlugError === null && !ooxmlUploadMutation.isPending;

  return (
    <SettingsPanel
      variant="card"
      icon={Upload}
      title={<Trans>Carica template grafico (compilabile)</Trans>}
      description={
        <Trans>
          Carica un .docx / .xlsx / .pptx brandizzato con segnaposto{" "}
          <code className="rounded bg-cream-deep px-1">{"{{nome}}"}</code>.
          L'AI lo riempirà preservando il tuo layout e i tuoi colori.
        </Trans>
      }
    >
      <div className="mt-1 flex flex-col gap-3">
        <label
          htmlFor="ooxml-file"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            handlePickOoxml(e.dataTransfer.files?.[0] ?? null);
          }}
          className={cn(
            "flex h-32 cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed text-center transition-colors",
            "focus-within:ring-2 focus-within:ring-ando-orange/40",
            ooxmlFile
              ? "border-ando-orange/50 bg-ando-orange-soft/30"
              : "border-hairline bg-card/40 hover:border-ando-orange/40 hover:bg-ando-orange-soft/20",
          )}
        >
          <Upload size={18} strokeWidth={1.75} className="text-ink-3" aria-hidden />
          <span className="font-medium text-[12px] text-ink-2">
            {ooxmlFile
              ? ooxmlFile.name
              : i18n._(msg`Trascina o seleziona .docx / .xlsx / .pptx`)}
          </span>
          {ooxmlFile ? (
            <span className="text-[10.5px] text-ink-3">
              {(ooxmlFile.size / 1024 / 1024).toFixed(2)} MB
            </span>
          ) : null}
          <input
            id="ooxml-file"
            type="file"
            accept=".docx,.xlsx,.pptx"
            className="sr-only"
            onChange={(e) => handlePickOoxml(e.target.files?.[0] ?? null)}
          />
        </label>
        {ooxmlError ? (
          <p role="alert" className="text-[11px] text-destructive">
            {ooxmlError}
          </p>
        ) : null}

        <FieldLabel htmlFor="ooxml-slug" required>
          <Trans>Nome (slug)</Trans>
        </FieldLabel>
        <input
          id="ooxml-slug"
          type="text"
          value={ooxmlSlug}
          onChange={(e) => setOoxmlSlug(e.target.value)}
          placeholder={i18n._(msg`offerta-commerciale`)}
          className="rounded-md border border-hairline bg-card px-2.5 py-1.5 text-[13px] text-ink-1 focus:outline-none focus:ring-2 focus:ring-ando-orange/40"
        />
        {ooxmlSlug.length > 0 && ooxmlSlugError ? (
          <p role="alert" className="text-[11px] text-destructive">
            {ooxmlSlugError}
          </p>
        ) : null}

        <fieldset className="flex flex-col gap-1.5">
          <legend className="font-medium text-[11px] text-ink-3 uppercase tracking-wide">
            <Trans>Scope</Trans>
          </legend>
          <div className="flex gap-2">
            <ScopeRadio
              name="ooxml-tpl-scope"
              value="user"
              checked={ooxmlScope === "user"}
              onChange={() => setOoxmlScope("user")}
              label={i18n._(msg`Solo per me`)}
            />
            <ScopeRadio
              name="ooxml-tpl-scope"
              value="org"
              checked={ooxmlScope === "org"}
              onChange={() => setOoxmlScope("org")}
              label={i18n._(msg`Tutta l'organizzazione`)}
            />
          </div>
        </fieldset>

        <div className="flex items-center justify-end">
          <Button
            type="button"
            onClick={() => ooxmlUploadMutation.mutate()}
            disabled={!canSubmit}
          >
            {ooxmlUploadMutation.isPending ? (
              <Loader2 size={14} strokeWidth={1.75} className="animate-spin" />
            ) : (
              <Upload size={14} strokeWidth={1.75} />
            )}
            {ooxmlUploadMutation.isPending
              ? i18n._(msg`Caricamento…`)
              : i18n._(msg`Carica template`)}
          </Button>
        </div>

        {((ooxmlListQuery.data?.templates.filter((t) => !t.is_builtin).length) ??
          0) > 0 ? (
          <ul className="mt-2 flex flex-col gap-1.5">
            {ooxmlListQuery.data?.templates
              .filter((t) => !t.is_builtin)
              .map((t) => (
                <li
                  key={`${t.scope}:${t.slug}`}
                  className="flex items-center gap-2 rounded-md border border-hairline bg-cream/30 px-3 py-1.5 text-[12px] text-ink-2"
                >
                  <span className="font-medium">{t.slug}</span>
                  <span className="text-ink-3">· {t.document_type ?? "?"}</span>
                  <span className="text-ink-3">· {t.scope}</span>
                  <span className="ml-auto text-ink-3">
                    <Trans>{t.placeholders.length} segnaposto</Trans>
                  </span>
                </li>
              ))}
          </ul>
        ) : null}
      </div>
    </SettingsPanel>
  );
};
OoxmlTemplatePanel.displayName = "MarpTemplateSettings.OoxmlTemplatePanel";
